/** Analytics data hygiene — used by both client and server adapters. Pure, isomorphic. */

/** Path-only URL: drop the query string and hash so tokens/PII never reach PostHog. */
export function sanitizeUrl(rawUrl: string): string {
  return rawUrl.split('#')[0].split('?')[0]
}

// email | VAT-like (2 letters + 2-12 alnum) | ?t=/&token= values
const REDACT = /([\w.+-]+@[\w.-]+\.\w+)|(\b[A-Z]{2}[0-9A-Z]{2,12}\b)|([?&](?:t|token)=[^&\s]+)/g

/** Redact emails, VAT-like ids, and ?t=/&token= values from free text. */
export function redactText(text: string): string {
  return text.replace(REDACT, '[redacted]')
}

/**
 * ZodError.message is a getter that returns JSON.stringify(this.issues, ...) — the full
 * issues payload (paths, codes, free-text .refine()/.custom() messages). ZodError.stack
 * also begins with that same serialized JSON. Neither can be safely redacted with the
 * narrow email/VAT/token regex, since arbitrary PII (e.g. a client legal name) can appear
 * in a custom issue message. So ZodErrors get a fixed safe message and a fresh stack.
 */
function isZodErrorShaped(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  if ((error as { name?: unknown }).name === 'ZodError') return true
  return Array.isArray((error as { issues?: unknown }).issues)
}

/**
 * Build a clean Error safe to send to PostHog: a fresh Error carries ONLY name + message
 * (+ redacted stack), dropping vendor-specific props like ZodError.errors that embed the
 * invalid input values (client names, VAT IDs, emails).
 */
export function toScrubbedError(error: unknown): Error {
  const name = error instanceof Error ? error.name : 'NonError'

  if (isZodErrorShaped(error)) {
    const clean = new Error('Validation failed')
    clean.name = name
    return clean
  }

  const message = error instanceof Error ? error.message : String(error)
  const clean = new Error(redactText(message))
  clean.name = name
  if (error instanceof Error && error.stack) clean.stack = redactText(error.stack)
  return clean
}
